import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {Fragment} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import LookupStepSeparatorIcon from 'lookup-config/LookupStepSeparatorIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import LoadingChip from 'ui-library/chips/LoadingChip';
import {NEAR_BLACK} from 'ui-library/Colors';

import {LookupConfigDescriptionQuery} from './__generated/LookupConfigDescriptionQuery';
import {
  LookupConfigModalContextInfo,
  LookupConfigSpec,
  LookupStepInfo,
  LookupStepType,
} from './LookupConfigModalConstants';
import {getLookupInfoArrayForLookupConfig} from './LookupConfigUtils';
import LookupStepChip from './LookupStepChip';
import {lookupStepsToInfosArray} from './LookupStepInitializerUtils';

interface LookupInfosDescriptionProps {
  lookupStepInfos: Array<LookupStepInfo>;
  shouldFadeChips?: boolean;
}

export function LookupInfosDescription(props: LookupInfosDescriptionProps): JSX.Element {
  const shouldParenthesize = (idx: number, step: number): boolean =>
    idx < props.lookupStepInfos.length - step &&
    props.lookupStepInfos[idx + step].lookupStep.type === LookupStepType.CONTAINER_BY_GRID_POSITION;
  return (
    <>
      {props.lookupStepInfos.map((lookupStepInfo, i) => (
        <Fragment key={i}>
          <LookupStepChip
            className={css(styles.lookupPath)}
            lookupStepInfo={lookupStepInfo}
            shouldFadeChip={props.shouldFadeChips}
            shouldParenthesize={{open: shouldParenthesize(i, 2), close: shouldParenthesize(i, 1)}}
          />
          {i !== props.lookupStepInfos.length - 1 && (
            <LookupStepSeparatorIcon index={i} lookupStepInfos={props.lookupStepInfos} />
          )}
        </Fragment>
      ))}
    </>
  );
}

export const LOOKUP_CONFIG_DESCRIPTION_QUERY: DocumentNode = gql`
  query LookupConfigDescriptionQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllAssaySchemasGraphQL
    ...AllStorableSchemasGraphQL
    ...AllCompleteTagSchemasGraphQL
    ...AllSchemaInterfacesGraphQL
    ...AllProcessTaskSchemasForSchemaConfigGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
  ${getFragmentWrapper('AllCompleteTagSchemas').fragment}
  ${getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig').fragment}
`;

export interface LookupConfigDescriptionProps {
  baseSchema: Schema | null;
  label?: string;
  lookupConfig: LookupConfigSpec;
  context?: LookupConfigModalContextInfo;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
}

export const LookupConfigDescription = (props: LookupConfigDescriptionProps): JSX.Element => {
  const label = props.label != null && (
    <strong className={css(styles.lookupConfigLabel)}>{props.label}:&nbsp;</strong>
  );
  // We need the schemas to be available in the cache before we call lookupStepsToInfosArray
  const result = useBenchlingQuery<LookupConfigDescriptionQuery>(LOOKUP_CONFIG_DESCRIPTION_QUERY);
  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        {label}
        <LoadingChip size={ChipSize.TABLE_CELL} />
      </>
    );
  }
  const lookupStepInfos = getLookupInfoArrayForLookupConfig(
    lookupStepsToInfosArray(
      props.lookupConfig.lookupSteps,
      props.baseSchema,
      props.context ?? {},
      props.schemaFieldFilterFn
    )
  );

  return (
    <>
      {label}
      <LookupInfosDescription lookupStepInfos={lookupStepInfos} />
    </>
  );
};

const styles = StyleSheet.create({
  lookupConfigLabel: {
    color: NEAR_BLACK,
    display: 'inline-block',
    fontSize: '13px',
    margin: '2px 0',
  },
  lookupPath: {
    margin: '2px 0',
  },
});
